"use client";

import { useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { buildPeel } from "@/lib/tool-kit/essay/peel";

type PeelFieldId = "point" | "evidence" | "explanation" | "link";

const PEEL_FIELD_IDS: PeelFieldId[] = [
  "point",
  "evidence",
  "explanation",
  "link",
];

type PeelValues = Record<PeelFieldId, string>;

const WORKED_EXAMPLE_IDS: PeelFieldId[] = [
  "point",
  "evidence",
  "explanation",
  "link",
];

/** English strings. A translated content file overrides these through `ui`. */
export const PEEL_PARAGRAPH_GENERATOR_STRINGS = {
  fieldLabels: {
    point: "Point",
    evidence: "Evidence",
    explanation: "Explanation",
    link: "Link",
  },
  fieldPlaceholders: {
    point:
      "e.g. Regular homework improves long-term retention of course material",
    evidence:
      "e.g. a statistic, quotation, or finding from your source, with the author and year",
    explanation:
      "e.g. revisiting material at home forces students to retrieve it from memory, which strengthens recall",
    link: "e.g. homework remains one of the most reliable tools schools have for deepening learning",
  },
  fieldTips: {
    point:
      "One clear claim that this paragraph will prove. It should directly support your thesis.",
    evidence:
      "A fact, statistic, quotation, or example from a credible source. Cite it in your required style.",
    explanation:
      "The analytical work: explain how the evidence proves the point. Never assume it speaks for itself.",
    link: "Tie the paragraph back to your thesis or set up the next paragraph's idea.",
  },
  assembleParagraph: "Assemble Paragraph",
  copied: "Copied!",
  copy: "Copy",
  fillInAllFourParts: "Fill in all four parts to assemble the paragraph.",
  outputLabel:
    "Your paragraph draft — adjust the connectives so it flows in your own voice",
  transitionForExample: "For example, ",
  transitionThisShows: "This shows that ",
  transitionUltimately: "Ultimately, ",
  explainerHeading: "What is the PEEL (or TEEL) structure?",
  explainerBody:
    "PEEL is a template for body paragraphs in essays: each paragraph makes one {point}, backs it with {evidence}, gives an {explanation} of how the evidence proves the point, and ends with a {link} back to the thesis or forward to the next idea. TEEL is the same structure — it just calls the first sentence a Topic sentence instead of a Point. The explanation is where the marks are earned: it is your analysis, not a summary of the evidence.",
  point: "Point",
  evidence: "Evidence",
  explanation: "Explanation",
  link: "Link",
  workedExample: "Worked example",
  workedExampleLabels: {
    point: "Point:",
    evidence: "Evidence:",
    explanation: "Explanation:",
    link: "Link:",
  },
  workedExampleSentences: {
    point: "School gardens improve students' attitudes toward healthy eating.",
    evidence:
      "For example, [your evidence: a statistic or quotation from your source, with its citation].",
    explanation:
      "This shows that hands-on involvement gives children a sense of ownership over food, which makes unfamiliar vegetables feel less intimidating than when they simply appear on a plate.",
    link: "Ultimately, gardens are a low-cost intervention that supports the wider case for practical food education in schools.",
  },
} as const;

export default function PeelParagraphGeneratorWidget() {
  const [values, setValues] = useState<PeelValues>({
    point: "",
    evidence: "",
    explanation: "",
    link: "",
  });
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(PEEL_PARAGRAPH_GENERATOR_STRINGS);
  const locale = useToolLocale();

  const canGenerate = PEEL_FIELD_IDS.every(
    (id) => values[id].trim().length > 0
  );

  function handleChange(id: PeelFieldId, value: string) {
    setValues((prev) => ({ ...prev, [id]: value }));
  }

  function handleGenerate() {
    if (!canGenerate) {
      return;
    }
    setOutput(
      buildPeel(
        values,
        {
          forExample: strings.transitionForExample,
          thisShows: strings.transitionThisShows,
          ultimately: strings.transitionUltimately,
        },
        locale
      )
    );
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Fields */}
      <section className="space-y-6">
        {PEEL_FIELD_IDS.map((id) => (
          <div className="space-y-2" key={id}>
            <label
              className="font-medium text-foreground text-sm"
              htmlFor={`peel-${id}`}
            >
              {strings.fieldLabels[id]}
            </label>
            <textarea
              className="min-h-[80px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id={`peel-${id}`}
              onChange={(e) => handleChange(id, e.target.value)}
              placeholder={strings.fieldPlaceholders[id]}
              value={values[id]}
            />
            <p className="text-muted-foreground text-xs">
              {strings.fieldTips[id]}
            </p>
          </div>
        ))}
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!canGenerate}
          onClick={handleGenerate}
          type="button"
        >
          {strings.assembleParagraph}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      {!canGenerate && (
        <p className="text-muted-foreground text-xs">
          {strings.fillInAllFourParts}
        </p>
      )}

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="peel-output"
          >
            {strings.outputLabel}
          </label>
          <textarea
            className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="peel-output"
            onChange={(e) => setOutput(e.target.value)}
            value={output}
          />
        </div>
      )}

      {/* Explainer */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.explainerHeading}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {renderSlots(strings.explainerBody, {
            point: <strong>{strings.point}</strong>,
            evidence: <strong>{strings.evidence}</strong>,
            explanation: <strong>{strings.explanation}</strong>,
            link: <strong>{strings.link}</strong>,
          })}
        </p>
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="font-medium text-foreground">
            {strings.workedExample}
          </h3>
          <div className="mt-3 space-y-3">
            {WORKED_EXAMPLE_IDS.map((id) => (
              <p
                className="text-muted-foreground text-sm leading-relaxed"
                key={id}
              >
                <span className="font-medium text-foreground">
                  {strings.workedExampleLabels[id]}
                </span>{" "}
                {strings.workedExampleSentences[id]}
              </p>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
